/**
 * Revenue and cost by month, as paired bars.
 *
 * Paired rather than stacked: stacking would make the total the visual
 * quantity, when the thing being read is the GAP between them -- that gap is
 * the margin. Two series, so a legend is always present.
 *
 * Deliberately one y-scale for both. Revenue and cost are the same unit, so a
 * second axis would be both unnecessary and misleading.
 */
import { useState } from "react";
import {
  ChartFrame,
  EmptyChart,
  GRID,
  Legend,
  niceBounds,
  SERIES_1,
  SERIES_2,
  useChartWidth,
} from "./chart-primitives";

const BASE_W = 720;
const H = 220;
const PAD = { top: 12, right: 16, bottom: 28, left: 56 };

export interface MonthMoney {
  label: string;
  revenue: number;
  cost: number;
  id?: string;
  provisional?: boolean;
}

const money = (n: number) =>
  n >= 1000 ? `$${Math.round(n / 1000).toLocaleString()}k` : `$${Math.round(n).toLocaleString()}`;

export function RevenueCostBars({
  months,
  onSelect,
  title = "Revenue and cost by month",
  subtitle,
}: {
  months: MonthMoney[];
  onSelect?: (m: MonthMoney) => void;
  title?: string;
  subtitle?: string;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const [frame, W] = useChartWidth(BASE_W);

  if (months.length === 0) {
    return (
      <ChartFrame title={title} subtitle={subtitle}>
        <EmptyChart message="No revenue or cost in this range" />
      </ChartFrame>
    );
  }

  const [lo, hi] = niceBounds(months.flatMap((m) => [m.revenue, m.cost]));
  const innerW = W - PAD.left - PAD.right;
  const innerH = H - PAD.top - PAD.bottom;
  const slot = innerW / months.length;
  // 2px of surface between adjacent fills so the pair reads as two marks.
  const barW = Math.max(3, Math.min(18, slot / 2 - 3));
  const y = (v: number) => PAD.top + innerH - ((v - lo) / (hi - lo || 1)) * innerH;
  const ticks = [lo, lo + (hi - lo) / 2, hi];
  const active = hover === null ? null : months[hover];

  return (
    <ChartFrame
      title={title}
      subtitle={subtitle}
      legend={
        <Legend
          items={[
            { label: "Revenue", color: SERIES_1 },
            { label: "Cost", color: SERIES_2 },
          ]}
        />
      }
    >
      <div ref={frame} className="relative w-full overflow-x-auto">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ minWidth: 420 }} role="img">
          <title>{title}</title>
          {ticks.map((t) => (
            <g key={t}>
              <line
                x1={PAD.left}
                x2={W - PAD.right}
                y1={y(t)}
                y2={y(t)}
                stroke={GRID}
                strokeWidth={1}
              />
              <text
                x={PAD.left - 8}
                y={y(t) + 3}
                textAnchor="end"
                className="fill-muted-foreground"
                style={{ fontSize: 10 }}
              >
                {money(t)}
              </text>
            </g>
          ))}

          {months.map((m, i) => {
            const cx = PAD.left + slot * i + slot / 2;
            const base = y(Math.max(lo, 0));
            return (
              <g
                key={m.label}
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover(null)}
                onClick={() => onSelect?.(m)}
                className={onSelect ? "cursor-pointer" : undefined}
                opacity={hover === null || hover === i ? 1 : 0.55}
              >
                <rect
                  x={PAD.left + slot * i}
                  y={PAD.top}
                  width={slot}
                  height={innerH}
                  fill="transparent"
                />
                {/* 4px rounded data-ends, anchored to the baseline. */}
                <rect
                  x={cx - barW - 1}
                  y={y(m.revenue)}
                  width={barW}
                  height={Math.max(1, base - y(m.revenue))}
                  rx={3}
                  fill={SERIES_1}
                />
                <rect
                  x={cx + 1}
                  y={y(m.cost)}
                  width={barW}
                  height={Math.max(1, base - y(m.cost))}
                  rx={3}
                  fill={SERIES_2}
                />
              </g>
            );
          })}

          {months.map((m, i) =>
            i % Math.ceil(months.length / 6) === 0 ? (
              <text
                key={`lbl-${m.label}`}
                x={PAD.left + slot * i + slot / 2}
                y={H - 8}
                textAnchor="middle"
                className="fill-muted-foreground"
                style={{ fontSize: 10 }}
              >
                {m.label.replace(/ \d{4}$/, "")}
              </text>
            ) : null,
          )}
        </svg>

        {active ? (
          <div
            className="pointer-events-none absolute top-0 rounded-md border bg-popover px-2 py-1 text-xs shadow-sm"
            style={{
              left: `${((PAD.left + slot * (hover as number) + slot / 2) / W) * 100}%`,
              transform: "translateX(-50%)",
            }}
          >
            <div className="font-medium text-popover-foreground">
              {active.label}
              {active.provisional ? " (forecast)" : ""}
            </div>
            <div className="tabular-nums text-muted-foreground">
              Revenue {money(active.revenue)}
            </div>
            <div className="tabular-nums text-muted-foreground">Cost {money(active.cost)}</div>
          </div>
        ) : null}
      </div>
    </ChartFrame>
  );
}
